const rawPieceConfigModules = import.meta.glob("./pieceConfigs/*.json", {
  eager: true,
  import: "default"
});

function getIdFromPath(filePath) {
  const match = String(filePath).match(/\/([^/]+)\.json$/);
  return match ? match[1] : "";
}

export function humanizePieceId(value) {
  if (typeof value !== "string") return "";

  return value
    .trim()
    .replace(/_/g, " ")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/\s+/g, " ")
    .trim();
}

function normalizePaletteType(pieceId, value) {
  const normalizedValue = typeof value === "string" ? value.trim().toLowerCase() : value;
  if (normalizedValue === "malik" || normalizedValue === "threat") {
    return normalizedValue;
  }

  return pieceId.startsWith("Malik_") ? "malik" : "threat";
}

function normalizeImageId(pieceId, value) {
  if (typeof value !== "string") {
    return pieceId;
  }

  const normalized = value
    .trim()
    .replace(/^.*[\\/]/, "")
    .replace(/\.png$/i, "");

  return normalized || pieceId;
}

function toBool(value, fallback = true) {
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value !== 0;
  if (typeof value === "string") {
    if (value === "true") return true;
    if (value === "false") return false;
  }
  return fallback;
}

function toPositiveInt(value, fallback = 1) {
  const parsed = Number.parseInt(value, 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
}

function normalizeHitPoints(rawConfig) {
  const rawHp =
    rawConfig?.hp ??
    rawConfig?.Hp ??
    rawConfig?.health ??
    rawConfig?.Health ??
    rawConfig?.maxLife ??
    rawConfig?.MaxLife;

  if (rawHp == null) {
    return null;
  }

  const parsed = Number.parseInt(rawHp, 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

function normalizeMinSharedThreatQuantity(rawConfig) {
  const rawValue =
    rawConfig?.minSharedThreatQuantity ??
    rawConfig?.MinSharedThreatQuantity;

  return toPositiveInt(rawValue, 1);
}

function normalizePieceSize(rawConfig) {
  const rawSize = rawConfig?.size ?? rawConfig?.Size;
  if (Array.isArray(rawSize)) {
    return Object.freeze({
      width: toPositiveInt(rawSize[0], 1),
      height: toPositiveInt(rawSize[1], 1)
    });
  }

  if (rawSize && typeof rawSize === "object") {
    return Object.freeze({
      width: toPositiveInt(rawSize.width ?? rawSize.Width, 1),
      height: toPositiveInt(rawSize.height ?? rawSize.Height, 1)
    });
  }

  const hasWidth =
    rawConfig?.width != null ||
    rawConfig?.Width != null ||
    rawConfig?.height != null ||
    rawConfig?.Height != null;

  if (hasWidth) {
    return Object.freeze({
      width: toPositiveInt(rawConfig?.width ?? rawConfig?.Width, 1),
      height: toPositiveInt(rawConfig?.height ?? rawConfig?.Height, 1)
    });
  }

  return null;
}

const PIECE_CONFIGS_BY_ID = Object.freeze(
  Object.entries(rawPieceConfigModules).reduce((configsById, [filePath, rawConfig]) => {
    const idFromPath = getIdFromPath(filePath);
    const configuredId = rawConfig?.id ?? rawConfig?.Id;
    const configuredImage = rawConfig?.image ?? rawConfig?.Image;
    const configuredDisplayName = rawConfig?.displayName ?? rawConfig?.DisplayName;
    const configuredType = rawConfig?.type ?? rawConfig?.Type;
    const configuredActive = rawConfig?.active ?? rawConfig?.Active;
    const configuredSize = normalizePieceSize(rawConfig);
    const configuredHp = normalizeHitPoints(rawConfig);
    const configuredSharedThreatQuantity = toBool(
      rawConfig?.sharedThreatQuantity ?? rawConfig?.SharedThreatQuantity,
      false
    );
    const configuredMinSharedThreatQuantity = normalizeMinSharedThreatQuantity(rawConfig);
    const pieceId =
      typeof configuredId === "string" && configuredId.trim()
        ? configuredId.trim()
        : idFromPath;

    if (!pieceId) {
      return configsById;
    }

    configsById[pieceId] = Object.freeze({
      id: pieceId,
      image: normalizeImageId(pieceId, configuredImage),
      displayName:
        typeof configuredDisplayName === "string" && configuredDisplayName.trim()
          ? configuredDisplayName.trim()
          : humanizePieceId(pieceId),
      type: normalizePaletteType(pieceId, configuredType),
      active: toBool(configuredActive, true),
      size: configuredSize ?? Object.freeze({ width: 1, height: 1 }),
      hp: configuredHp,
      sharedThreatQuantity: configuredSharedThreatQuantity,
      minSharedThreatQuantity: configuredMinSharedThreatQuantity
    });

    return configsById;
  }, {})
);

function createMalikSummonRule(spawnType, description, contribution = 1, options = {}) {
  const parsedCooldown = Number.parseInt(options?.cooldownTurns, 10);
  const cooldownTurns =
    Number.isFinite(parsedCooldown) && parsedCooldown > 0 ? parsedCooldown : null;
  const trigger =
    options?.trigger === "death"
      ? "death"
      : options?.trigger === "interval"
        ? "interval"
        : cooldownTurns != null
          ? "interval"
          : "unknown";

  return Object.freeze({
    spawnType,
    description,
    contribution: Number.isFinite(Number(contribution))
      ? Math.max(1, Number(contribution))
      : 1,
    cooldownTurns,
    trigger
  });
}

const MALIK_SUMMON_RULES_BY_TYPE = Object.freeze({
  Malik_Ghost: Object.freeze([
    createMalikSummonRule(
      "Honey_0",
      "Frontline summon: spreads down its lane.",
      1,
      { cooldownTurns: 10, trigger: "interval" }
    )
  ]),
  Malik_Bone_Golem: Object.freeze([
    createMalikSummonRule(
      "Honey_0",
      "Frontline summon: spreads down its lane.",
      1,
      { cooldownTurns: 7, trigger: "interval" }
    )
  ]),
  Malik_Skeleton: Object.freeze([
    createMalikSummonRule(
      "WoodenBox_1",
      "Back summoner: summons on random valid tiles.",
      1,
      { cooldownTurns: 4, trigger: "interval" }
    )
  ]),
  Malik_Bone_Naga: Object.freeze([
    createMalikSummonRule(
      "WoodenBox_0",
      "Frontline summon: spreads down its lane.",
      1,
      { cooldownTurns: 100, trigger: "interval" }
    ),
    createMalikSummonRule(
      "Malik_Snake",
      "Death summon: spawns 5 when destroyed.",
      5,
      { trigger: "death" }
    )
  ]),
  Malik_Zombie: Object.freeze([
    createMalikSummonRule(
      "WoodenBox_0",
      "Frontline summon: summons on random valid tiles.",
      1,
      { cooldownTurns: 100, trigger: "interval" }
    )
  ]),
  Malik_Snake: Object.freeze([
    createMalikSummonRule(
      "WoodenBox_0",
      "Frontline summon: spreads down its lane.",
      1,
      { cooldownTurns: 100, trigger: "interval" }
    )
  ]),
  Malik_Necrotic_Egg: Object.freeze([
    createMalikSummonRule(
      "Malik_Bone_Golem",
      "Death summon: spawns 1 when destroyed.",
      1,
      { trigger: "death" }
    )
  ]),
  Malik_Haunted_House: Object.freeze([
    createMalikSummonRule(
      "Malik_Ghost",
      "Back summoner: summons.",
      1,
      { cooldownTurns: 4, trigger: "interval" }
    )
  ]),
  Malik_Snail: Object.freeze([
    createMalikSummonRule(
      "Honey_0",
      "Frontline summon: spawns on random valid tiles.",
      1,
      { cooldownTurns: 100, trigger: "interval" }
    )
  ])
});

const MALIK_SUMMON_RULE_ALIASES = Object.freeze({
  Malik_Wall_One_Silver: "Malik_Wall_One",
  Malik_Skull_Slime_Weak: "Malik_Skull_Slime",
  Malik_Skull_Big_Slime_Weak: "Malik_Skull_Big_Slime"
});

function resolveMalikSummonRuleType(pieceId) {
  if (typeof pieceId !== "string") {
    return "";
  }

  const normalizedId = pieceId.trim();
  if (!normalizedId) {
    return "";
  }

  return MALIK_SUMMON_RULE_ALIASES[normalizedId] ?? normalizedId;
}

function buildFallbackConfig(pieceId) {
  return Object.freeze({
    id: pieceId,
    image: pieceId,
    displayName: humanizePieceId(pieceId),
    type: normalizePaletteType(pieceId, null),
    active: true,
    size: Object.freeze({ width: 1, height: 1 }),
    hp: null,
    sharedThreatQuantity: false,
    minSharedThreatQuantity: 1
  });
}

export function getPieceConfig(pieceId) {
  if (typeof pieceId !== "string") {
    return buildFallbackConfig("");
  }

  const normalizedId = pieceId.trim();
  if (!normalizedId) {
    return buildFallbackConfig("");
  }

  return PIECE_CONFIGS_BY_ID[normalizedId] ?? buildFallbackConfig(normalizedId);
}

export function getPieceDisplayName(pieceId) {
  return getPieceConfig(pieceId).displayName;
}

export function getPieceImage(pieceId) {
  return getPieceConfig(pieceId).image;
}

export function getPiecePaletteType(pieceId) {
  return getPieceConfig(pieceId).type;
}

export function isPieceActive(pieceId) {
  return getPieceConfig(pieceId).active;
}

export function getConfiguredPieceSize(pieceId) {
  if (typeof pieceId !== "string") {
    return null;
  }

  const normalizedId = pieceId.trim();
  if (!normalizedId) {
    return null;
  }

  return PIECE_CONFIGS_BY_ID[normalizedId]?.size ?? null;
}

export function getPieceHitPoints(pieceId) {
  return getPieceConfig(pieceId).hp;
}

export function usesSharedThreatQuantity(pieceId) {
  return getPieceConfig(pieceId).sharedThreatQuantity;
}

export function getPieceMinSharedThreatQuantity(pieceId) {
  return getPieceConfig(pieceId).minSharedThreatQuantity;
}

export function isMalikPalettePiece(pieceId) {
  return getPiecePaletteType(pieceId) === "malik";
}

export function getMalikSummonRules(pieceId) {
  const resolvedType = resolveMalikSummonRuleType(pieceId);
  return MALIK_SUMMON_RULES_BY_TYPE[resolvedType] ?? [];
}

export function listPieceConfigs() {
  return Object.values(PIECE_CONFIGS_BY_ID);
}
